/**
 * Damage / missing-item flags derived from a stored receiving-log row.
 * -------------------------------------------------------------------
 * A stored log row has no structured damage/missing booleans — that information
 * is folded INLINE into the free-text `items` string when the log is built
 * (see `buildItemsString`). A kept-damaged line reads "(DAMAGED BUT KEEPING)",
 * a refused/returned line reads "(DAMAGED, REFUSED)" / "(DAMAGED, RETURNED)",
 * and outstanding items sit under a "MISSING LINE ITEM(S) …" clause.
 *
 * These helpers recover those flags for read-only surfaces (the log table's
 * damage indicator and the resend-email preview) by scanning that string.
 * Matching is case-insensitive so hand-typed desktop entries ("damaged") are
 * caught alongside the machine-built upper-cased strings.
 */

/** True when the row's items text mentions damaged goods (any disposition). */
export function itemsHaveDamage(items?: string | null): boolean {
  if (!items) {
    return false;
  }
  return /damaged/i.test(items);
}

/**
 * True when the row's items text carries a MISSING clause — i.e. the delivery
 * had outstanding items (plain shortfalls or refused/returned goods), which is
 * exactly what triggers the separate expediting email.
 */
export function itemsHaveMissing(items?: string | null): boolean {
  if (!items) {
    return false;
  }
  return /missing/i.test(items);
}

/**
 * True when the delivery would have generated an expediting (missing-items)
 * email. The expediting email fires on MISSING items — plain shortfalls OR
 * refused/returned goods. Refused/returned goods always also read as "DAMAGED",
 * and both land in the MISSING clause, so a MISSING clause is the reliable
 * signal. (Kept-damaged-only deliveries have no missing items and no expediting
 * email — see the damaged-items email handoff.)
 */
export function itemsHaveExpeditingEmail(items?: string | null): boolean {
  return itemsHaveMissing(items);
}
